import { useDataExplorerAppearance } from './appearanceContext';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { DataExplorerButton } from './DataExplorerButton';
import { DataExplorerCheckbox } from './DataExplorerCheckbox';
import { DataExplorerSelect } from './DataExplorerSelect';
import type {
  DataExplorerToolbarAction,
  DataExplorerToolbarActionItem,
  DataExplorerToolbarActionsProps,
} from './types';
import {
  getDataExplorerMenuItemToneClassName,
  resolveDataExplorerAppearance,
} from './theme';
import { cn, resolveDataExplorerValue } from './utils';

type ResolvedTheme = ReturnType<typeof resolveDataExplorerAppearance>;

function ChevronDownIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function resolveActionDisabled<TContext>(
  action: DataExplorerToolbarAction<TContext>,
  context: TContext | undefined,
) {
  return (
    resolveDataExplorerValue(action.disabled, context) ??
    resolveDataExplorerValue(action.loading, context) ??
    false
  );
}

function getActionCallback<TContext>(
  action: DataExplorerToolbarAction<TContext>,
) {
  return action.onAction ?? action.onClick;
}

function renderMenuItem<TContext>(
  action: DataExplorerToolbarActionItem<TContext>,
  context: TContext | undefined,
  theme: ResolvedTheme,
  className?: string,
) {
  const disabled =
    (resolveDataExplorerValue(action.disabled, context) ?? false) ||
    (resolveDataExplorerValue(action.loading, context) ?? false);
  const active = resolveDataExplorerValue(action.active, context) ?? false;

  return (
    <div key={action.id}>
      {action.separatorBefore ? (
        <DropdownMenu.Separator className={theme.slots.menuSeparator} />
      ) : null}
      <DropdownMenu.Item
        title={action.title}
        disabled={disabled}
        className={cn(
          theme.slots.menuItem,
          getDataExplorerMenuItemToneClassName(theme, action.tone),
          active && 'bg-accent text-accent-foreground',
          className,
        )}
        style={theme.styles.menuItem}
        onSelect={() => {
          if (!action.onAction || disabled) return;
          void action.onAction(context as TContext);
        }}
      >
        {action.icon ? (
          <span className={theme.slots.menuItemIcon}>{action.icon}</span>
        ) : null}
        <span className={theme.slots.menuItemLabel}>{action.label}</span>
      </DropdownMenu.Item>
      {action.separatorAfter ? (
        <DropdownMenu.Separator className={theme.slots.menuSeparator} />
      ) : null}
    </div>
  );
}

function renderMenuAction<TContext>(
  action: DataExplorerToolbarAction<TContext>,
  context: TContext | undefined,
  theme: ResolvedTheme,
  classNames: DataExplorerToolbarActionsProps<TContext>['classNames'],
  appearance: DataExplorerToolbarActionsProps<TContext>['appearance'],
) {
  const disabled = resolveActionDisabled(action, context);
  const active = resolveDataExplorerValue(action.active, context) ?? false;
  const visibleItems = (action.items ?? []).filter(
    (item) => resolveDataExplorerValue(item.visible, context) ?? true,
  );

  return (
    <DropdownMenu.Root key={action.id}>
      <DropdownMenu.Trigger asChild>
        <DataExplorerButton
          title={action.title}
          ariaLabel={action.triggerLabel}
          disabled={disabled || visibleItems.length === 0}
          label={action.label}
          icon={action.icon}
          endIcon={<ChevronDownIcon />}
          tone={action.tone}
          active={active}
          stopPropagation
          appearance={appearance}
          className={classNames?.action}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={action.align ?? 'end'}
          sideOffset={action.sideOffset ?? 6}
          className={cn(theme.slots.menuContent, classNames?.menuContent)}
          style={theme.styles.menuContent}
          onClick={(event) => event.stopPropagation()}
        >
          {action.menuLabel ? (
            <>
              <DropdownMenu.Label className={theme.slots.menuLabel}>
                {action.menuLabel}
              </DropdownMenu.Label>
              <DropdownMenu.Separator className={theme.slots.menuSeparator} />
            </>
          ) : null}
          {visibleItems.map((item) =>
            renderMenuItem(item, context, theme, classNames?.menuItem),
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function renderSelectAction<TContext>(
  action: DataExplorerToolbarAction<TContext>,
  context: TContext | undefined,
  classNames: DataExplorerToolbarActionsProps<TContext>['classNames'],
  appearance: DataExplorerToolbarActionsProps<TContext>['appearance'],
) {
  const disabled = resolveActionDisabled(action, context);
  const value = String(resolveDataExplorerValue(action.value, context) ?? '');
  const options = action.options ?? [];

  return (
    <DataExplorerSelect
      key={action.id}
      value={value}
      label={action.label}
      icon={action.icon}
      title={action.title}
      triggerLabel={action.triggerLabel}
      disabled={disabled}
      display={action.selectDisplay ?? 'label-value'}
      align={action.align ?? 'end'}
      sideOffset={action.sideOffset ?? 6}
      options={options.map((option) => ({
        value: option.value,
        label: option.label,
        icon: option.icon,
        title: option.title,
        disabled: resolveDataExplorerValue(option.disabled, context) ?? false,
      }))}
      appearance={appearance}
      classNames={{
        trigger: classNames?.action,
        content: classNames?.menuContent,
        item: classNames?.menuItem,
      }}
      onValueChange={(nextValue) => {
        if (!action.onValueChange || disabled) return;
        void action.onValueChange(nextValue, context as TContext);
      }}
    />
  );
}

function renderButtonAction<TContext>(
  action: DataExplorerToolbarAction<TContext>,
  context: TContext | undefined,
  appearance: DataExplorerToolbarActionsProps<TContext>['appearance'],
  className?: string,
) {
  const kind = action.kind ?? 'button';
  const active =
    kind === 'toggle'
      ? resolveDataExplorerValue(action.checked, context) ??
        resolveDataExplorerValue(action.active, context) ??
        false
      : resolveDataExplorerValue(action.active, context) ?? false;
  const disabled = resolveActionDisabled(action, context);
  const callback = getActionCallback(action);

  if (kind === 'toggle') {
    return (
      <DataExplorerCheckbox
        key={action.id}
        checked={active}
        icon={action.icon}
        label={action.label}
        title={action.title}
        disabled={disabled}
        appearance={appearance}
        className={className}
        onCheckedChange={(checked) => {
          if (action.onCheckedChange) {
            void action.onCheckedChange(checked, context as TContext);
          }
        }}
      />
    );
  }

  return (
    <DataExplorerButton
      key={action.id}
      label={action.label}
      icon={action.icon}
      title={action.title}
      ariaLabel={kind === 'icon-button' ? action.triggerLabel || action.title : undefined}
      tone={action.tone}
      active={active}
      iconOnly={kind === 'icon-button'}
      disabled={disabled}
      loading={resolveDataExplorerValue(action.loading, context) ?? false}
      stopPropagation
      appearance={appearance}
      className={className}
      onClick={() => {
        if (!callback) return;
        void callback(context as TContext);
      }}
    />
  );
}

export function DataExplorerToolbarActions<TContext = unknown>({
  context,
  actions = [],
  layout = 'action-bar',
  appearance,
  empty,
  children,
  className,
  classNames,
}: DataExplorerToolbarActionsProps<TContext>) {
  const theme = resolveDataExplorerAppearance(
    useDataExplorerAppearance(appearance),
  );
  const renderedActions = actions
    .filter((action) => resolveDataExplorerValue(action.visible, context) ?? true)
    .map((action) => {
      if (action.render) {
        return <div key={action.id}>{action.render(context as TContext)}</div>;
      }
      if (action.renderTrigger) {
        return <div key={action.id}>{action.renderTrigger(context as TContext)}</div>;
      }
      const renderedAction =
        action.kind === 'menu'
          ? renderMenuAction(action, context, theme, classNames, appearance)
          : action.kind === 'select'
            ? renderSelectAction(action, context, classNames, appearance)
            : renderButtonAction(action, context, appearance, classNames?.action);

      if (!action.wrapTrigger) {
        return renderedAction;
      }

      return (
        <div key={action.id}>
          {action.wrapTrigger(renderedAction, context as TContext)}
        </div>
      );
    });

  if (renderedActions.length === 0 && !children && !empty) return null;

  const layoutClassName =
    layout === 'action-bar'
      ? theme.slots.actionBarActions
      : layout === 'toolbar'
        ? theme.slots.toolbarActions
        : 'flex items-center gap-2';

  return (
    <div className={cn(layoutClassName, classNames?.root, className)}>
      {renderedActions}
      {children}
      {renderedActions.length === 0 && !children ? empty : null}
    </div>
  );
}
